/*
 * Palco-scoped sessionStorage persistence.
 *
 * REGRA (aprovada): persistência aplica APENAS ao slice `state.palco`. O
 * wizard antigo (/parcel/, /bike/parcel/) está em produção e nunca foi
 * desenhado para re-hidratação - ligar persistência global re-hidrataria
 * drafts obsoletos em fluxos que não estão sob o escopo deste rebrand.
 *
 * Naming convention da storage key: `palco:<product>:<version>`. Cada
 * produto tem a sua key porque:
 *   - user está num produto por sessão (bike OU sport), nunca ambos;
 *   - se um dia rodar entre produtos, cada draft sobrevive na sua key;
 *   - torna óbvio no DevTools que a persistência é por-produto.
 *
 * A whitelist explícita (PALCO_PERSISTED_KEYS) é a garantia estrutural
 * de scope: mesmo que alguém acidentalmente passe o estado inteiro para
 * save, só as keys aqui listadas atravessam. E se sessionStorage tiver
 * chaves extras de versões futuras/passadas, são ignoradas no load.
 *
 * `loading` fica DELIBERADAMENTE fora - persistir um loader ativo faria o
 * palco ficar preso em "a procurar ofertas..." depois de um refresh, sem
 * request nenhum a correr.
 *
 * Version-bump: se o shape do PalcoState mudar de forma incompatível
 * (nova key required, rename de campo), incrementar o `v1` para descartar
 * drafts velhos automaticamente sem migração.
 */

import type { PalcoState, StageId } from './palco';

/** Namespace convention: `palco:<product>:<version>`. Cada produto tem a sua.
 * v2: bumped quando removemos `type: {cadre, motor}` per-shipment do slice
 * e migrámos para per-bike. Sessions em v1 tinham bikes sem cadre/motor -
 * incompatível com o novo shape. Bump descarta v1 automaticamente. */
export const PALCO_STORAGE_KEY = 'palco:colibike:v2';

/**
 * Whitelist explícita das keys de PalcoState que persistem. Inclui chassis
 * (stage, offerSubStep, visitedStages) + slot do produto (colibike). Quando
 * Colisport for adicionado, `colisport` juntar-se-ia aqui - mas apenas se
 * essa storage key `palco:colisport:v1` for a que estamos a salvar (cada
 * página só persiste o SEU produto; ver whichSliceToSave).
 *
 * Adicionar uma key NOVA a PalcoState requer um pensamento consciente
 * ("faz sentido re-hidratar isto quando o utilizador reabre o tab?").
 */
const PALCO_PERSISTED_KEYS = [
  'stage',
  'offerSubStep',
  'visitedStages',
  'colibike',
] as const satisfies ReadonlyArray<keyof PalcoState>;

/**
 * Stages terminais que NUNCA devem ser re-hidratados. Se um utilizador
 * completou o flow e voltou ao tab, dar-lhe o palco em 'done' ou 'paiement'
 * confunde - reset para 'type' e deixa o resto do draft intacto para ele
 * poder criar um novo envio a partir do zero.
 *
 * Item 14 (correções pós-reunião): 'recap' e 'paiement' TAMBÉM não re-hidratam.
 * O dinheiro deles (deliveryPrice/pickUpDate/contactos) vive no slice LEGACY
 * `steps`, que por regra NÃO persiste - depois de um reload o recap renderizava
 * a 0,00 € e o Payer criava uma order Revolut REAL a 0 € (provado na sandbox:
 * HTTP 201, amount 0). Re-hidratar estes stages é SEMPRE errado; aterra-se em
 * 'quantite' com o draft das bikes intacto.
 */
const NON_REHYDRATABLE_STAGES: readonly StageId[] = [
  'done',
  'loader-pay',
  'loader-offers',
  'recap',
  'paiement',
];

/**
 * Item 14: o visitedStages persistido só pode reivindicar stages cujos DADOS
 * também persistem (slice palco: quantité/origine/destination). 'offer',
 * 'contact', 'recap', 'paiement' vivem do slice `steps` (morto após reload) -
 * mantê-los deixava o advance('recap') do passo 1 SALTAR direto ao recap a
 * zeros pela âncora de visitados (o "começo um vélo novo e aterro no RECAP"
 * do registo). Filtrar no load mata a âncora na raiz: o avanço volta a ser
 * linear e os preços renascem no caminho normal (loader de ofertas).
 */
const REHYDRATABLE_VISITED: readonly StageId[] = ['quantite', 'origine', 'destination'];

const isBrowser = (): boolean => typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';

/**
 * Lê o slice palco do sessionStorage e devolve um patch parcial pronto a
 * dispatch com UPDATE_PALCO. Devolve `null` se não houver nada, ou se o
 * conteúdo for inválido / corrompido / de uma versão incompatível.
 *
 * Não lança - qualquer erro (JSON malformado, storage inacessível) é
 * silenciosamente ignorado e a app arranca com o initialPalcoState.
 */
export const loadPalcoFromSession = (): Partial<PalcoState> | null => {
  if (!isBrowser()) return null;
  try {
    const raw = window.sessionStorage.getItem(PALCO_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;

    // Filtrar pela whitelist - keys extras (potencial ataque XSS via
    // sessionStorage, ou drift de shape) NÃO atravessam.
    const clean: Partial<PalcoState> = {};
    for (const key of PALCO_PERSISTED_KEYS) {
      if (key in parsed) {
        (clean as any)[key] = parsed[key];
      }
    }

    // Safety: não re-hidratar terminal stages. Reset para o primeiro stage
    // user-visible do Colibike ('quantite' após Fase 2 - antes era 'type').
    if (clean.stage && NON_REHYDRATABLE_STAGES.includes(clean.stage)) {
      clean.stage = 'quantite';
    }

    // Item 14: âncoras de visitados sem dados por trás não atravessam o reload.
    if (Array.isArray(clean.visitedStages)) {
      clean.visitedStages = clean.visitedStages.filter((s: StageId) =>
        REHYDRATABLE_VISITED.includes(s));
    }

    return Object.keys(clean).length > 0 ? clean : null;
  } catch {
    // Corrupted JSON, quota exceeded, or storage unavailable - just start fresh.
    return null;
  }
};

/**
 * Escreve o slice palco no sessionStorage, filtrando pela mesma whitelist.
 * Aceita PalcoState completo (chamado do useEffect com state.palco); só as
 * keys whitelisted são serializadas.
 *
 * Não lança - se sessionStorage estiver cheio ou desactivado (Safari
 * private mode), o save falha silenciosamente e a UX continua a funcionar
 * (só perde a garantia de sobreviver refresh).
 */
export const savePalcoToSession = (palco: PalcoState): void => {
  if (!isBrowser()) return;
  try {
    const clean: Partial<PalcoState> = {};
    for (const key of PALCO_PERSISTED_KEYS) {
      (clean as any)[key] = palco[key];
    }
    window.sessionStorage.setItem(PALCO_STORAGE_KEY, JSON.stringify(clean));
  } catch {
    // sessionStorage full or unavailable - drop silently.
  }
};

/**
 * Limpa o slice palco persistido do sessionStorage sem tocar em nenhuma outra
 * key. Usado pelo reset do palco (usePalco.reset) e pelo PalcoInspector.
 *
 * `storageKey` é PARAMETRIZÁVEL (default = PALCO_STORAGE_KEY) porque o reset do
 * chassis é AGNÓSTICO AO PRODUTO: recebe de fora a chave a limpar (o Colibike
 * passa `palco:colibike:v2`; o Colisport passaria a sua). Assim o chassis nunca
 * hardcoda uma chave de produto - ver usePalco.reset e [[project_palco_chassis_refactor_debt]].
 */
export const clearPalcoFromSession = (storageKey: string = PALCO_STORAGE_KEY): void => {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.removeItem(storageKey);
  } catch {
    // no-op
  }
};
